/**
 * EntityImage Component - Renders images using Object URLs from IndexedDB
 * 
 * Uses the useObjectUrl hook to properly manage object URL lifecycle:
 * - Creates object URL only when fileId changes
 * - Revokes object URL on unmount
 * - Prevents memory leaks
 * 
 * Supports click-to-open lightbox when onClick is provided
 */
import React, { memo, useState } from 'react';
import { useObjectUrl } from '../hooks/useObjectUrl';
import ImageLightbox from './ImageLightbox';

/**
 * Single image component that renders from a file ID
 * @param {string} fileId - File ID or image URL
 * @param {string} alt - Alt text
 * @param {string} className - CSS classes
 * @param {React.ReactNode} fallback - Fallback content when no image
 * @param {boolean} enableLightbox - Enable click-to-open lightbox (default: false)
 */
export const EntityImage = memo(function EntityImage({ 
  fileId, 
  alt = '', 
  className = '',
  fallback = null,
  enableLightbox = false,
  ...imgProps 
}) {
  const url = useObjectUrl(fileId);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Handle case where fileId is actually already a data URL or http URL
  const isDataUrl = fileId && typeof fileId === 'string' && fileId.startsWith('data:');
  const isHttpUrl = fileId && typeof fileId === 'string' && (fileId.startsWith('http://') || fileId.startsWith('https://'));
  const isBlobUrl = fileId && typeof fileId === 'string' && fileId.startsWith('blob:');
  
  // Use direct URL if it's already a renderable format
  const src = isDataUrl || isHttpUrl || isBlobUrl ? fileId : url;
  
  if (!src) {
    return fallback;
  }
  
  const handleClick = () => {
    if (enableLightbox) {
      setLightboxOpen(true);
    }
  };
  
  return (
    <>
      <img 
        src={src} 
        alt={alt} 
        className={`${className} ${enableLightbox ? 'cursor-pointer' : ''}`}
        onClick={handleClick}
        {...imgProps}
      />
      {enableLightbox && (
        <ImageLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          imageFileIds={[fileId]}
          alt={alt}
        />
      )}
    </>
  );
});

/**
 * Thumbnail component for list views - shows first image from array
 * @param {string[]} imageFileIds - Array of image file IDs
 * @param {string} alt - Alt text
 * @param {string} className - CSS classes for the image
 * @param {React.ReactNode} fallback - Fallback content
 * @param {string} containerClassName - CSS classes for the container
 * @param {boolean} enableLightbox - Enable click-to-open lightbox (default: false)
 */
export const EntityThumbnail = memo(function EntityThumbnail({
  imageFileIds,
  alt = '',
  className = '',
  fallback = null,
  containerClassName = '',
  enableLightbox = false,
  ...imgProps
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Get first image file ID
  const firstFileId = imageFileIds?.[0];
  
  if (!firstFileId) {
    return (
      <div className={containerClassName}>
        {fallback}
      </div>
    );
  }
  
  const handleClick = () => {
    if (enableLightbox && imageFileIds?.length > 0) {
      setLightboxOpen(true);
    }
  };
  
  return (
    <div className={containerClassName}>
      <EntityImage
        fileId={firstFileId}
        alt={alt}
        className={`${className} ${enableLightbox ? 'cursor-pointer' : ''}`}
        fallback={fallback}
        onClick={handleClick}
        {...imgProps}
      />
      {enableLightbox && (
        <ImageLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          imageFileIds={imageFileIds}
          alt={alt}
        />
      )}
    </div>
  );
});

/**
 * Image gallery component for detail pages
 * Supports click-to-open lightbox for full-screen viewing
 * @param {string[]} imageFileIds - Array of image file IDs
 * @param {string} alt - Alt text
 * @param {string} className - CSS classes for main image container
 * @param {string} thumbnailClassName - CSS classes for thumbnail images
 * @param {React.ReactNode} fallback - Fallback content
 * @param {boolean} enableLightbox - Enable click-to-open lightbox (default: true)
 */
export function EntityImageGallery({
  imageFileIds,
  alt = '',
  className = '',
  thumbnailClassName = '',
  fallback = null,
  enableLightbox = true
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  if (!imageFileIds || imageFileIds.length === 0) {
    return (
      <div className={className}>
        {fallback}
      </div>
    );
  }
  
  const selectedFileId = imageFileIds[selectedIndex];
  
  const handleMainImageClick = () => {
    if (enableLightbox) {
      setLightboxOpen(true);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Main Image - clickable for lightbox */}
      <div 
        className={`${className} ${enableLightbox ? 'cursor-pointer' : ''}`}
        onClick={handleMainImageClick}
      >
        <EntityImage
          fileId={selectedFileId}
          alt={alt}
          className="w-full h-full object-contain"
          fallback={fallback}
        />
      </div>
      
      {/* Thumbnails */}
      {imageFileIds.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {imageFileIds.map((fileId, idx) => (
            <button
              key={fileId || idx}
              onClick={() => setSelectedIndex(idx)}
              className={`w-20 h-16 flex-shrink-0 rounded-sm overflow-hidden border-2 transition-colors ${
                selectedIndex === idx 
                  ? 'border-secondary' 
                  : 'border-border hover:border-muted-foreground'
              }`}
            >
              <EntityImage
                fileId={fileId}
                alt=""
                className={thumbnailClassName || "w-full h-full object-cover"}
              />
            </button>
          ))}
        </div>
      )}
      
      {/* Lightbox */}
      {enableLightbox && (
        <ImageLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          imageFileIds={imageFileIds}
          initialIndex={selectedIndex}
          alt={alt}
        />
      )}
    </div>
  );
}

export default EntityImage;
